import { useCallback, useState } from "react";
import { usePageLoader } from "../../../../common/contexts/page_content_context";
import { useCreateDebounceHandler, useDebouncedCallback } from "../../../../common/hooks/use_debounced_callback";
import { useNavigate } from "react-router";
import { processError } from "../../../../common/utils/base_error";
import { useDependencyInjection } from "../../../../config/di/dependency_injection_context";
import { usePageStartBootstrap } from "../../../../common/hooks/use_page_start_bootstrap";
import { DropDown } from "../../../../common/domain/models/dropdown";
import { SubcontaLista } from "../../domain/models/subconta_lista";
import { initialSubcontaListaFiltersValues, useSubcontaListaFilters } from "../hooks/subconta_lista/filters";
import { useSubcontaListaPagination } from "../hooks/subconta_lista/pagination";
import { Subconta } from "../../domain/models/subconta";
import { SubcontaForm } from "../schemas/Subconta_lista/cadastro_subconta";
import { LogTipoTabela } from "../../../../common/utils/log_tabela_tipo";
import { useChangeHistory } from "../../../../common/hooks/use_change_history";

type LoadSubcontaListaState = {
    codigo?: string,
    descricao?: string,
    status?: number,

    currentPage?: number;
    rowsPerPage?: number;

}

export const useSubcontasListaViewModel = () => {

    const {
        comercial: {
            getAllSubcontasUseCase,
            deleteSubcontasUseCase,
            createSubcontasUseCase,
            updateSubcontasUseCase,
            getSubcontasByIdUseCase,

        },
        commons: {
            getSubcontasDropDownUseCase,
            getAllLogAlteracaoUseCase,
            getAllLogAlteracaoByParentUseCase,
        },

    } = useDependencyInjection();


    const navigate = useNavigate();
    const { pushErrorNotification, pushWarnNotification, pushInfoNotification, setPageLoading } = usePageLoader();
    const debouncedRef = useCreateDebounceHandler(1500);

    const [openDeleteModal, setOpenDeleteModal] = useState(false);
    const [selectedIdSubconta, setSelectedIdSubconta] = useState<number>();
    const [selectedSubconta, setSelectedSubconta] = useState<Subconta>();

    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const [errorMessage, setErrorMessage] = useState<string>();
    const [subcontasLista, setSubcontasLista] = useState<SubcontaLista[]>();
    const [ddlSubconta, setDdlSubconta] = useState<DropDown[]>();

    const [openCadastroModal, setOpenCadastroModal] = useState(false);

    const filters = useSubcontaListaFilters();
    const pagination = useSubcontaListaPagination();

    const changeHistory = useChangeHistory({
        getAllLogAlteracaoUseCase,
        getAllLogAlteracaoByParentUseCase,
        onSetError: (e) => pushErrorNotification('Erro', [e]),
    })

    //#region CARREGAMENTO.

    const loadSubcontasLista = useCallback(async (funcState?: LoadSubcontaListaState) => {

        try {
            const filteredCodigo = funcState?.codigo ?? filters.codigo;
            const filteredDescricao = funcState?.descricao ?? filters.descricao;
            const filteredStatus = funcState?.status ?? filters.status;

            const filteredPage = funcState?.currentPage ?? pagination.page;
            const filteredRowsPerPage = funcState?.rowsPerPage ?? pagination.rowsPerPage;

            await getAllSubcontasUseCase.execute(
                filteredCodigo,
                filteredDescricao,
                filteredStatus,

                filteredPage,
                filteredRowsPerPage,
            );

            setSubcontasLista(getAllSubcontasUseCase.items);

            pagination.setRowsPerPage(getAllSubcontasUseCase.pageSize);
            pagination.setTotalItems(getAllSubcontasUseCase.totalItems);
        } catch (e: unknown) {
            setSubcontasLista([]);
            pushErrorNotification("Error", processError(e, "Falha ao buscar lista de subcontas!"));
        }
    }, [
        filters,
        pagination,
        getAllSubcontasUseCase,
        pushErrorNotification
    ]);

    const loadSubcontaById = useCallback(async (idSubconta: number) => {
        try {
            setPageLoading(true);
            const response = await getSubcontasByIdUseCase.execute(idSubconta);
            setSelectedSubconta(response);
        } catch (e: unknown) {
            setSelectedSubconta(undefined);
            pushErrorNotification('Error', processError(e, 'Falha ao buscar Subconta por Id!'));
        }
    }, [getSubcontasByIdUseCase,
        pushErrorNotification]);

    const loadSubcontaDropDown = useCallback(async () => {
        try {
            setPageLoading(true);
            const response = await getSubcontasDropDownUseCase.getSubcontasDropDown();
            setDdlSubconta(response);
        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao buscar Subconta!'));
        } finally {
            setPageLoading(false);
        }
    }, [getSubcontasDropDownUseCase,
        pushErrorNotification]);


    //#endregion

    //#region START BOOTSTRAP.


    usePageStartBootstrap(async () => {
        try {
            if (subcontasLista === undefined) {
                await loadSubcontasLista();
                await loadSubcontaDropDown();
            }
        } catch (e: unknown) {
            setErrorMessage(processError(e, "Falha ao buscar lista de subcontas!")[0]);
        }
    });

    //#endregion


    //#region NOVO EDITAR MODAL.

    const handleEditSubcontasOpenState = useCallback(async (idSubconta: number | null, openState: boolean) => {
        if (openState) {
            if (idSubconta) {
                setSelectedIdSubconta(idSubconta);
                await loadSubcontaById(idSubconta);
                setOpenCadastroModal(true);
            } else {

                setSelectedIdSubconta(undefined);
                setSelectedSubconta(undefined);
                setOpenCadastroModal(true);
            }
        } else {
            setSelectedIdSubconta(undefined);
            setOpenCadastroModal(false);
        }
    }, [
        loadSubcontaById
    ]);


    //#endregion



    //#region PAGINAÇÃO.


    const handleReload = useCallback(async () => {
        try {
            setLoading(true);
            await loadSubcontasLista();
        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao buscar lista de subcontas!'));
        } finally {
            setLoading(false);
        }
    }, [loadSubcontasLista]);

    const handlePageChange = useCallback(async (page: number) => {
        try {
            setLoading(true);
            pagination.setPage(page + 1);
            await loadSubcontasLista({ currentPage: page + 1 });
        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao buscar lista de subcontas!'));
        } finally {
            setLoading(false);
        }
    }, [pagination, loadSubcontasLista]);

    const handleRowsPerPageChange = useCallback(async (rowsPerPage: number) => {
        try {
            setLoading(true);
            pagination.setPage(1);
            pagination.setRowsPerPage(rowsPerPage);
            await loadSubcontasLista({ rowsPerPage, currentPage: 1 });
        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao buscar lista de subcontas!'));
        } finally {
            setLoading(false);
        }
    }, [pagination, loadSubcontasLista]);

    //#endregion



    //#region  SALVAR/ UPDATE 

    const handleSalvarSubcontas = useCallback(async (subconta: SubcontaForm) => {
        try {
            setSaving(true);
            if (selectedSubconta) {
                await updateSubcontasUseCase.execute(
                    selectedSubconta.idSubconta,
                    subconta.ativo,
                    subconta.idSubcontaPai ?? undefined,
                    subconta.codigo,
                    subconta.descricao
                );
                pushInfoNotification("Sucesso", " Subconta atualizado com sucesso!", { isAutoFinish: true });
            } else {
                await createSubcontasUseCase.execute(
                    subconta.ativo,
                    subconta.idSubcontaPai ?? undefined,
                    subconta.codigo,
                    subconta.descricao
                );
                pushInfoNotification("Sucesso", " Subconta criado com sucesso!", { isAutoFinish: true });
            }
            await loadSubcontaDropDown()
            await loadSubcontasLista()
        } catch (e: unknown) {
            pushErrorNotification("Erro", processError(e, "Falha ao salvar Subconta!"));
        } finally {
            setSaving(false);
            setOpenCadastroModal(false);
            setSelectedIdSubconta(undefined);
            setSelectedSubconta(undefined);
        }
    }, [
        selectedSubconta,
        selectedIdSubconta,
        createSubcontasUseCase,
        updateSubcontasUseCase,
        loadSubcontaDropDown,
        loadSubcontasLista,
        pushErrorNotification,
        pushInfoNotification
    ]);


    //#region DELECAO

    const handleSubcontasDeleteOpenState = useCallback((idSubconta: number | null, openState: boolean) => {
        if (openState) {
            if (idSubconta) {
                setSelectedIdSubconta(idSubconta);
                setOpenDeleteModal(true);
            } else {
                setSelectedIdSubconta(undefined);
                setOpenDeleteModal(false);
            }
        }
        setOpenDeleteModal(openState);
    }, []);


    const handleConfirmarDeletarSubcontas = useCallback(async () => {
        try {

            setSaving(true);
            if (selectedIdSubconta) {
                await deleteSubcontasUseCase.execute(selectedIdSubconta);
                pushInfoNotification("Sucesso", " Subconta deletado com sucesso!", { isAutoFinish: true });
            } else {
                pushErrorNotification("Erro", "Não foi possivel deletar Subconta!", { isAutoFinish: true });

            }
            await loadSubcontaDropDown()
            await loadSubcontasLista()
        } catch (e: unknown) {
            pushErrorNotification("Erro", processError(e, "Falha ao Deletar Subconta!"));
        } finally {
            setSaving(false);
            setOpenDeleteModal(false);
            setSelectedIdSubconta(undefined)
        }
    }, [
        selectedIdSubconta,
        deleteSubcontasUseCase,
        loadSubcontaDropDown,
        loadSubcontasLista,
        pushErrorNotification,
        pushInfoNotification]);


    //#endregion





    //#region PESQUISA.


    const handleCodigoChange = useDebouncedCallback(
        debouncedRef,
        (newValue: string | null) => {

            filters.setCodigo(newValue ?? undefined);
            return async () => {
                try {
                    setLoading(true);
                    pagination.setPage(1);
                    await loadSubcontasLista({ codigo: newValue ?? undefined, currentPage: 1 });
                } catch (e: unknown) {
                    pushErrorNotification("Erro", processError(e, "Falha ao buscar subcontas!"));
                } finally {
                    setLoading(false);
                }
            };
        },
        [filters, pagination, loadSubcontasLista, pushErrorNotification]
    );


    const handleDescricaoChange = useDebouncedCallback(
        debouncedRef,
        (newValue: string | null) => {

            filters.setDescricao(newValue ?? undefined);
            return async () => {
                try {
                    setLoading(true);
                    pagination.setPage(1);
                    await loadSubcontasLista({ descricao: newValue ?? undefined, currentPage: 1 });
                } catch (e: unknown) {
                    pushErrorNotification("Erro", processError(e, "Falha ao buscar subcontas!"));
                } finally {
                    setLoading(false);
                }
            };
        },
        [filters, pagination, loadSubcontasLista, pushErrorNotification]
    );

    const handleStatusChange = useDebouncedCallback(
        debouncedRef,
        (newValue: number | null) => {

            filters.setStatus(newValue ?? undefined);
            return async () => {
                try {
                    setLoading(true);
                    pagination.setPage(1);
                    await loadSubcontasLista({ status: newValue ?? undefined, currentPage: 1 });
                } catch (e: unknown) {
                    pushErrorNotification("Erro", processError(e, "Falha ao buscar subcontas!"));
                } finally {
                    setLoading(false);
                }
            };
        },
        [filters, pagination, loadSubcontasLista, pushErrorNotification]);



    const handleClearFilters = useCallback(async () => {
        try {
            setLoading(true);
            filters.clearFilters();
            pagination.setPage(1);
            await loadSubcontasLista({
                ...initialSubcontaListaFiltersValues,
                currentPage: 1
            });
        } catch (e: unknown) {
            pushErrorNotification("Erro", processError(e, 'Falha ao buscar subcontas!'));
        } finally {
            setLoading(false);
        }
    }, [
        filters,
        pagination,
        loadSubcontasLista,
        pushErrorNotification]);

    //#endregion 


    //#region HISTORICO DE ALTERAÇÕES
    const handleOpenChangeHistory = useCallback((
        idTabela: number | null,
        tipoTabela: LogTipoTabela | null,
    ) => {
        if (idTabela && tipoTabela) {
            changeHistory.handleOpenChangeHistory(idTabela, tipoTabela, false);
        } else {
            if (true) {
                changeHistory.handleOpenChangeHistory(selectedSubconta?.idSubconta ?? 0, LogTipoTabela.SUBCONTA, true);
            }
        }
    }, [
        selectedSubconta,
        changeHistory,
    ]);

    //#endregion

    return {
        loading,
        saving,
        changeHistory,
        subcontasLista: subcontasLista ?? [],
        errorMessage,
        filters,
        pagination,
        openDeleteModal,
        ddlSubconta: ddlSubconta ?? [],
        selectedIdSubconta,
        openCadastroModal,
        selectedSubconta,


        handleOpenChangeHistory,
        handleSalvarSubcontas,
        handleEditSubcontasOpenState,
        handleReload,
        handlePageChange,
        handleRowsPerPageChange,
        handleClearFilters,
        handleCodigoChange,
        handleDescricaoChange,
        handleStatusChange,
        handleSubcontasDeleteOpenState,
        handleConfirmarDeletarSubcontas,

    };
}


